import React, { useState } from 'react';
import { FileText } from 'lucide-react';
import Button from '../../components/ui/Button';
import StatusBadge from '../../components/StatusBadge';
import { ErrorState } from '../../components/ui/States';
import { useConfirm } from '../../components/ui/ConfirmProvider';
import { getApiBaseUrl } from '../../lib/apiClient';
import {
  DebitNote,
  approveVendorAmount,
  attachVendorCreditNote,
  declineVendorAmount,
  getVendorCreditNoteUrl,
  keepOurAmount,
  requestVendorAmount,
} from '../../lib/debitNoteApi';
import { formatDate } from '../../utils/dateFormat';
import { errorMessageOf, formatDebitNoteMoney } from './debitNoteDisplay';
import DateInput from "../../components/DateInput";

/**
 * The supplier's own credit note against a vendor return (D44 #16).
 *
 * It arrives once the return has gone to the vendor, so it can be attached from
 * Sent onwards. A person types its number, date and amount off the document; the
 * server compares that amount with ours and, when they differ, the flag is
 * settled one of two ways: "Keep ours" (with a note saying why) or "Use vendor's
 * amount", which only an Admin can approve because it changes what is credited.
 */

/** Where the server accepts the vendor's credit note (debitNote.service VENDOR_CN_STATUSES). */
const VENDOR_CN_STATUSES = ['sent', 'awaiting_approval', 'approved'];

const inputClass = 'w-full h-8 rounded-md border border-slate-200 px-2 text-xs focus:border-primary-500';
const labelClass = 'block text-xs font-medium text-slate-600 mb-1';

/** The date as the calendar day it was typed: stored at UTC midnight, so read in UTC, not local time. */
function calendarDay(value: string | null | undefined): string {
  return value ? String(value).slice(0, 10) : '';
}

export const VendorCreditNotePanel: React.FC<{
  note: DebitNote;
  isAdmin: boolean;
  canEdit: boolean;
  onUpdated: (next: DebitNote) => void;
}> = ({ note, isAdmin, canEdit, onUpdated }) => {
  const confirm = useConfirm();
  const cn: NonNullable<DebitNote['vendorCreditNote']> = note.vendorCreditNote || {};
  const attached = Boolean(cn.attachedAt || cn.number);
  const acceptsNote = VENDOR_CN_STATUSES.includes(note.status);
  const check = note.amountCheck?.state ?? null;
  // Once the vendor's amount is with an Admin or in use, the server refuses a new copy.
  const replaceable = acceptsNote && check !== 'use_vendor_requested' && check !== 'use_vendor_approved';

  const [editing, setEditing] = useState(false);
  const [number, setNumber] = useState('');
  const [date, setDate] = useState('');
  // D109.6: false while the date box holds a refused date (`date` is then the OLD one).
  const [dateValid, setDateValid] = useState(true);
  const [amount, setAmount] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [checkNote, setCheckNote] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!attached && !acceptsNote) return null;

  const currency = note.currency;
  const money = (value: number | null | undefined) => formatDebitNoteMoney(value, currency);
  const showForm = canEdit && replaceable && (!attached || editing);
  // The file is required the first time; correcting the typed details keeps the file already attached.
  const formReady = Boolean(dateValid && number.trim() && date && amount.trim() && (attached || file));

  const run = async (name: string, fallback: string, call: () => Promise<DebitNote>): Promise<boolean> => {
    setBusy(name);
    setError(null);
    try {
      onUpdated(await call());
      return true;
    } catch (e) {
      setError(errorMessageOf(e, fallback));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const startEditing = () => {
    setNumber(cn.number || '');
    setDate(calendarDay(cn.date));
    setAmount(typeof cn.amount === 'number' ? String(cn.amount) : '');
    setFile(null);
    setEditing(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formReady) return;
    const ok = await run('attach', "The vendor's credit note could not be attached.", () =>
      attachVendorCreditNote(note._id, { number: number.trim(), date, amount: amount.trim(), file })
    );
    if (ok) {
      setEditing(false);
      setFile(null);
    }
  };

  // The link is signed and expires in minutes, so it is minted on click and opened
  // at once. It comes back server-relative (`/api/...`) and is made absolute with
  // lib/apiClient's getApiBaseUrl, as the vendor invoice attachment link is.
  const openFile = async () => {
    setError(null);
    try {
      const path = await getVendorCreditNoteUrl(note._id);
      const url = /^https?:\/\//i.test(path) ? path : `${getApiBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(errorMessageOf(e, "The vendor's credit note could not be opened. Try again."));
    }
  };

  const approveVendors = async () => {
    const ok = await confirm({
      title: "Use the vendor's amount?",
      summary: `${note.debitNoteNumber} will credit ${money(note.amountCheck?.vendorAmount)} instead of ${money(
        note.amountCheck?.ourAmount
      )}.${note.status === 'approved' ? ' The bill is corrected by the difference now.' : ''}`,
      confirmLabel: "Approve vendor's amount",
    });
    if (!ok) return;
    await run('approve_vendor', "The vendor's amount could not be approved.", () => approveVendorAmount(note._id));
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-semibold text-slate-700">Vendor credit note</div>
        {!attached && <StatusBadge status="Vendor credit note pending" tone="attention" />}
      </div>

      {error && <ErrorState message={error} />}

      {attached && !editing && (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs sm:grid-cols-4">
            <div>
              <dt className="text-slate-500">Their number</dt>
              <dd className="font-medium text-slate-800">{cn.number || '--'}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Date</dt>
              <dd className="font-medium text-slate-800">{formatDate(calendarDay(cn.date), '--')}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Amount</dt>
              <dd className="font-medium tabular-nums text-slate-800">{money(cn.amount)}</dd>
            </div>
            <div>
              <dt className="text-slate-500">File</dt>
              <dd className="font-medium text-slate-800 break-all">{cn.fileName || '--'}</dd>
            </div>
          </dl>
          <div className="flex flex-wrap gap-2">
            {cn.fileName && (
              <Button size="sm" onClick={openFile} icon={<FileText aria-hidden="true" className="h-4 w-4" />}>
                View file
              </Button>
            )}
            {canEdit && replaceable && (
              <Button size="sm" variant="ghost" onClick={startEditing}>
                Correct details
              </Button>
            )}
          </div>
        </div>
      )}

      {!attached && !showForm && (
        <p className="text-xs text-slate-500">Waiting for the vendor&apos;s credit note.</p>
      )}

      {showForm && (
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label htmlFor="vendor-cn-number" className={labelClass}>
                Their credit note number
              </label>
              <input id="vendor-cn-number" className={inputClass} value={number} onChange={(e) => setNumber(e.target.value)} />
            </div>
            <div>
              <label htmlFor="vendor-cn-date" className={labelClass}>
                Date on their credit note
              </label>
              <DateInput id="vendor-cn-date" className={inputClass} value={date} onChange={(v) => setDate(v)} validity={setDateValid} />
            </div>
            <div>
              <label htmlFor="vendor-cn-amount" className={labelClass}>
                Amount ({currency}, VAT included)
              </label>
              <input
                id="vendor-cn-amount"
                type="number"
                min="0"
                step="0.01"
                className={inputClass}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="vendor-cn-file" className={labelClass}>
                File {attached ? '(leave empty to keep the one attached)' : ''}
              </label>
              <input
                id="vendor-cn-file"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                className="block w-full text-xs text-slate-600"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" variant="primary" size="sm" loading={busy === 'attach'} disabled={!formReady || busy !== null}>
              Attach
            </Button>
            {editing && (
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={busy !== null}>
                Keep what is attached
              </Button>
            )}
            {!formReady && (
              <span className="text-xs text-slate-500">
                {!dateValid
                  ? 'Fix the date first: it is not a real day.'
                  : attached ? 'Number, date and amount are needed.' : 'Number, date, amount and the file are needed.'}
              </span>
            )}
          </div>
        </form>
      )}

      {check === 'mismatch' && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 space-y-2">
          <p className="text-xs font-medium text-amber-900">
            The vendor&apos;s credit note says {money(note.amountCheck?.vendorAmount)}; ours is{' '}
            {money(note.amountCheck?.ourAmount)}.
          </p>
          {canEdit && (
            <>
              <div>
                <label htmlFor="vendor-cn-check-note" className={labelClass}>
                  Note on the amount
                </label>
                <textarea
                  id="vendor-cn-check-note"
                  rows={2}
                  maxLength={1000}
                  value={checkNote}
                  onChange={(e) => setCheckNote(e.target.value)}
                  placeholder="Required for Keep ours: why our amount stands"
                  className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs focus:border-primary-500 resize-y"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() =>
                    run('use_vendor', "The vendor's amount could not be requested.", () =>
                      requestVendorAmount(note._id, checkNote.trim())
                    )
                  }
                  loading={busy === 'use_vendor'}
                  disabled={busy !== null}
                  title="Asks an Admin to credit the vendor's amount instead of ours"
                >
                  Use vendor&apos;s amount
                </Button>
                <Button
                  size="sm"
                  onClick={() =>
                    run('keep_ours', 'Our amount could not be kept.', () => keepOurAmount(note._id, checkNote.trim()))
                  }
                  loading={busy === 'keep_ours'}
                  disabled={!checkNote.trim() || busy !== null}
                  title={checkNote.trim() ? undefined : 'Type a note saying why our amount stands'}
                >
                  Keep ours
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {check === 'use_vendor_requested' &&
        (isAdmin ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 space-y-2">
            <p className="text-xs font-medium text-amber-900">
              Someone asked to credit the vendor&apos;s amount, {money(note.amountCheck?.vendorAmount)}, instead of ours,{' '}
              {money(note.amountCheck?.ourAmount)}.
              {note.amountCheck?.note ? ` Note: ${note.amountCheck.note}` : ''}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="primary" onClick={approveVendors} loading={busy === 'approve_vendor'} disabled={busy !== null}>
                Approve vendor&apos;s amount
              </Button>
              <Button
                size="sm"
                onClick={() =>
                  run('decline_vendor', "The vendor's amount could not be declined.", () => declineVendorAmount(note._id))
                }
                loading={busy === 'decline_vendor'}
                disabled={busy !== null}
              >
                Decline
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-600">Waiting for an Admin to approve the vendor&apos;s amount.</p>
        ))}

      {check === 'use_vendor_approved' && <p className="text-xs text-slate-600">The vendor&apos;s amount is used.</p>}

      {check === 'kept' && <p className="text-xs text-slate-600">Our amount kept: {note.amountCheck?.note || '--'}</p>}
    </div>
  );
};

export default VendorCreditNotePanel;
